import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { ChakraProvider } from '@chakra-ui/react'
import { HelmetProvider } from 'react-helmet-async'
import theme from './theme'
import { AuthProvider } from './contexts/AuthContext'
import { SocialProvider } from './contexts/SocialContext'
import Layout from './components/Layout'
import PrivateRoute from './components/PrivateRoute'
import Admin from './pages/Admin'
import Home from './pages/Home'
import CFOPMethod from './pages/CFOPMethod'
import OLLMethod from './pages/OLLMethod'
import PLLMethod from './pages/PLLMethod'
import MovesNotation from './pages/MovesNotation'
import History from './pages/History'
import HistoryArticle from './pages/HistoryArticle'
import ApiDocumentation from './pages/ApiDocumentation'

// Configure React Router future flags
const router = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
};

// Lazy load components
const Login = lazy(() => import('./pages/Login'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const Register = lazy(() => import('./pages/Register'))
const Profile = lazy(() => import('./pages/Profile'))
const PublicProfile = lazy(() => import('./pages/PublicProfile'))
const Timer = lazy(() => import('./pages/Timer'))
const ArticlePage = lazy(() => import('./pages/ArticlePage'))
const Hub = lazy(() => import('./pages/Hub'))
const SharedScrambles = lazy(() => import('./pages/SharedScrambles'))
const SharedScrambleView = lazy(() => import('./components/SharedScrambleView'))
const BeginnersMethod = lazy(() => import('./pages/BeginnersMethod'))
const Statistics = lazy(() => import('./pages/Statistics'))
const Cubes = lazy(() => import('./pages/Cubes'))
const CubeDetails = lazy(() => import('./pages/CubeDetails'))
const TournamentMode = lazy(() => import('./pages/TournamentMode'))
const AlgorithmTraining = lazy(() => import('./pages/AlgorithmTraining'))
const CaseTraining = lazy(() => import('./pages/CaseTraining'))
const HardwareTest = lazy(() => import('./pages/hardware-test'))
const SolverPage = lazy(() => import('./pages/SolverPage'))

// Social layer pages
const Feed = lazy(() => import('./pages/Feed'))
const Battles = lazy(() => import('./pages/Battles'))
const BattleRoom = lazy(() => import('./pages/BattleRoom'))
const Messages = lazy(() => import('./pages/Messages'))
const Leaderboard = lazy(() => import('./pages/Leaderboard'))

function App() {
  return (
    <>
      <HelmetProvider>
        <ChakraProvider theme={theme}>
          <AuthProvider>
            <SocialProvider>
              <Router {...router}>
                <Suspense>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                      <Route path="/u/:username" element={<PublicProfile />} />
                      <Route path="/timer" element={<Timer />} />
                      <Route path="/timer/zen" element={<Timer />} />
                      <Route path="/statistics" element={<PrivateRoute><Statistics /></PrivateRoute>} />
                      <Route path="/tournament" element={<PrivateRoute><TournamentMode /></PrivateRoute>} />
                      <Route path="/history" element={<History />} />
                      <Route path="/history/:articleId" element={<HistoryArticle />} />
                      <Route path="/hub" element={<Hub />} />
                      <Route path="/hub/:slug" element={<ArticlePage />} />
                      <Route path="/shared" element={<SharedScrambles />} />
                      <Route path="/shared/scramble/:id" element={<SharedScrambleView />} />
                      <Route path="/beginners-method" element={<BeginnersMethod />} />
                      <Route path="/beginners-method/:step" element={<BeginnersMethod />} />
                      <Route path="/beginners-method/:step/:algorithmId" element={<BeginnersMethod />} />
                      <Route path="/cfop" element={<CFOPMethod />} />
                      <Route path="/oll" element={<OLLMethod />} />
                      <Route path="/pll" element={<PLLMethod />} />
                      <Route path="/moves-notation" element={<MovesNotation />} />
                      <Route path="/api-docs" element={<PrivateRoute requireAdmin><ApiDocumentation /></PrivateRoute>} />
                      <Route path="/hardware-test" element={<PrivateRoute requireAdmin><HardwareTest /></PrivateRoute>} />
                      <Route path="/admin/*" element={<PrivateRoute requireAdmin><Admin /></PrivateRoute>} />
                      <Route path="/cubes" element={<Cubes />} />
                      <Route path="/cubes/:slug" element={<CubeDetails />} />
                      <Route path="/training" element={<AlgorithmTraining />} />
                      <Route path="/training/case/:caseId" element={<CaseTraining />} />
                      <Route path="/solve" element={<SolverPage />} />
                      {/* Social layer routes */}
                      <Route path="/feed" element={<PrivateRoute><Feed /></PrivateRoute>} />
                      <Route path="/battles" element={<Battles />} />
                      <Route path="/battles/:battleId" element={<PrivateRoute><BattleRoom /></PrivateRoute>} />
                      <Route path="/leaderboard" element={<Leaderboard />} />
                      <Route path="/messages" element={<PrivateRoute><Messages /></PrivateRoute>} />
                    </Routes>
                  </Layout>
                </Suspense>
              </Router>
            </SocialProvider>
          </AuthProvider>
        </ChakraProvider>
      </HelmetProvider>
    </>
  )
}

export default App
